import { useState, useEffect, useRef } from 'react';
import type { Card } from '@mtg-deck-tool/shared';
import { api } from '../lib/api-client.js';

const DEBOUNCE_MS = 150;
const MIN_CHARS = 2;

/**
 * Returns true if the text contains Scryfall operator syntax,
 * meaning autocomplete should be suppressed.
 */
export function hasOperatorSyntax(text: string): boolean {
  return /[:<>=!]/.test(text);
}

export function useAutocomplete(input: string) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [cardsByName, setCardsByName] = useState<Map<string, Card>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    abortRef.current?.abort();

    // Suppress autocomplete when the bar contains Scryfall syntax operators
    if (input.length < MIN_CHARS || hasOperatorSyntax(input)) {
      setSuggestions([]);
      setCardsByName(new Map());
      return;
    }

    timerRef.current = setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;
      setIsLoading(true);
      api
        .autocomplete(input)
        .then(async ({ suggestions: s }) => {
          if (controller.signal.aborted) return;
          setSuggestions(s);
          if (s.length === 0) {
            setCardsByName(new Map());
            return;
          }
          // Batch-resolve suggestions to full Card objects so the dropdown
          // can show a preview image and an add-to-deck button without
          // requiring the user to run a full search first.
          try {
            const { found } = await api.resolveCards(s);
            if (controller.signal.aborted) return;
            const map = new Map<string, Card>();
            for (const card of found) {
              map.set(card.name.toLowerCase(), card);
            }
            setCardsByName(map);
          } catch {
            if (!controller.signal.aborted) setCardsByName(new Map());
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setSuggestions([]);
            setCardsByName(new Map());
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setIsLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, [input]);

  const clear = () => {
    setSuggestions([]);
    setCardsByName(new Map());
  };

  return { suggestions, cardsByName, isLoading, clear };
}
